import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { CandidateEngagementsConst } from '@axiom/const';
import {
  Card,
  CardSection,
  CondensedLarge,
  Gutter,
  Layout,
  useApi,
  PluralizeUtil,
  CondensedHeader,
  ReadonlyTextarea,
} from '@axiom/ui';
import { isLpUser } from '@axiom/utils';

import { CandidateEngagementsApi } from '../../api/candidate-engagements-api';
import { useCandidateId } from '../../hooks/useCandidateId';
import { EngagementHeader } from '../EngagementHeader/EngagementHeader';
import type { EngagementsHistorySearchRequestTabValues } from '../../schema/engagements-history-search-request-schema';
import { EngagementsUtil } from '../../utils/engagements-util';
import { EngagementBadges } from '../EngagementBadges/EngagementBadges';
import { UserApi } from '../../api/user-api';
import { SubmissionDetails } from '../SubmissionDetails/SubmissionDetails';

import { EngagementsHistoryViewPagination } from './EngagementsHistoryViewPagination';
import { EngagementsHistoryViewStatusMessageBanner } from './EngagementsHistoryViewStatusMessageBanner';

export type EngagementsHistoryViewContentProps = {
  name?: string;
  viewType: EngagementsHistorySearchRequestTabValues;
};

export const EngagementsHistoryViewContent: React.FC<
  EngagementsHistoryViewContentProps
> = ({ name = 'ENGAGEMENTS_HISTORY_VIEW_CONTENT', viewType }) => {
  const candidateId = useCandidateId();
  const { pageNumber: urlPageNumber } = useParams();

  const pageNumber = useMemo(() => {
    const parsedPageNumber = urlPageNumber
      ? Number.parseInt(urlPageNumber, 10)
      : 1;
    return Number.isNaN(parsedPageNumber) ? 1 : parsedPageNumber;
  }, [urlPageNumber]);

  const [{ data: user }, { data: engagements, meta }] = useApi(
    UserApi.readUser(),
    CandidateEngagementsApi.readEngagementsHistory(candidateId, {
      page: pageNumber,
      pageSize: 10,
      viewType,
    })
  );

  useEffect(() => {
    if (!isLpUser(user)) {
      return;
    }
    const markAsViewed = async () => {
      const opportunityIds = engagements
        .filter(engagement =>
          EngagementsUtil.getBadgeConfig({ engagement }).some(
            badgeConfig =>
              badgeConfig.badge === CandidateEngagementsConst.Badges.New
          )
        )
        .map(engagement => engagement.opportunity.id);
      if (opportunityIds.length > 0) {
        await CandidateEngagementsApi.createEngagementsViewed(candidateId, {
          opportunityIds,
        });
        await CandidateEngagementsApi.refreshEngagementsCount(candidateId);
      }
    };
    void markAsViewed();
  }, [engagements, candidateId]);

  return engagements.length > 0 ? (
    <Layout name={name} direction="vertical">
      <Layout position="space-between middle" direction="horizontal">
        <CondensedLarge name="TOTAL_OPPORTUNITY_COUNT">
          {`${meta.resultCount} ${PluralizeUtil({
            count: meta.resultCount,
            text: 'opportunity',
            options: {
              zero: 'opportunities',
              many: 'opportunities',
            },
          })}`}
        </CondensedLarge>

        <EngagementsHistoryViewPagination
          meta={meta}
          name="ENGAGEMENTS_HISTORY_VIEW_PAGINATION_TOP"
        />
      </Layout>
      <Gutter bottom="16px" />
      <Layout direction="vertical" verticalGutter="16px">
        {engagements.map((engagement, i) => (
          <Card
            key={engagement.opportunity.id}
            name={`ENGAGEMENT_HISTORY_CARD_${i}`}
          >
            <EngagementHeader
              name={`ENGAGEMENT_HEADER_${i}`}
              engagement={engagement}
              divider
            />
            <CardSection>
              {engagement.candidateStatusMessage ||
              engagement.rejectionLossCode ? (
                <EngagementsHistoryViewStatusMessageBanner
                  engagement={engagement}
                  name={`ENGAGEMENTS_HISTORY_VIEW_STATUS_MESSAGE_BANNER_${i}`}
                />
              ) : null}
              {EngagementsUtil.getBadgeConfig({ engagement }).length > 0 ? (
                <Gutter bottom="24px">
                  <EngagementBadges
                    engagement={engagement}
                    name={`ENGAGEMENT_BADGES_${i}`}
                  />
                </Gutter>
              ) : null}
              <SubmissionDetails
                name={`ENGAGEMENT_DETAILS_${i}`}
                submission={engagement}
              />
              <Gutter bottom="16px" />
              <CondensedHeader>Work Description</CondensedHeader>
              <Gutter bottom="4px" />
              <ReadonlyTextarea
                value={engagement.position?.description || '--'}
                name={`WORK_DESCRIPTION_${i}`}
                formatting="modern"
              />
            </CardSection>
          </Card>
        ))}
      </Layout>
      <Gutter top="16px">
        <Layout position="right">
          <EngagementsHistoryViewPagination
            meta={meta}
            name="ENGAGEMENTS_HISTORY_VIEW_PAGINATION_BOTTOM"
          />
        </Layout>
      </Gutter>
    </Layout>
  ) : null;
};
